import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { WorkspaceWhitelabelManager } from '@/components/workspace/WorkspaceWhitelabelManager';
import { Building, Globe, Shield, Palette, AlertTriangle, Sparkles } from 'lucide-react';

export default function WorkspaceSettings() {
  const { currentWorkspace } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(tabParam);

  useEffect(() => {
    const tab = searchParams.get('tab') || 'general';
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workspace Settings</h1>
          <p className="text-muted-foreground">Configure your workspace preferences and settings</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">
            <Building className="h-4 w-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="branding">
            <Palette className="h-4 w-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="whitelabel">
            <Sparkles className="h-4 w-4 mr-2" />
            Whitelabel
          </TabsTrigger>
          <TabsTrigger value="security">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">

          <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5" />
              <span>General</span>
            </CardTitle>
            <CardDescription>Basic workspace information and settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="workspaceName">Workspace Name</Label>
                <Input 
                  id="workspaceName" 
                  value={currentWorkspace?.name || ''}
                  placeholder="Enter workspace name" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="workspaceSlug">Workspace URL</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 text-sm text-muted-foreground bg-muted border border-r-0 rounded-l-md">
                    billportal.app/
                  </span>
                  <Input 
                    id="workspaceSlug" 
                    value={currentWorkspace?.slug || ''}
                    placeholder="workspace-url" 
                    className="rounded-l-none"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                placeholder="Describe your workspace..."
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="utc">UTC</SelectItem>
                  <SelectItem value="est">Eastern Time</SelectItem>
                  <SelectItem value="pst">Pacific Time</SelectItem>
                  <SelectItem value="gmt">GMT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end">
              <Button>Save Changes</Button>
            </div>
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="branding" className="space-y-6">
          <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Palette className="h-5 w-5" />
              <span>Branding</span>
            </CardTitle>
            <CardDescription>Customize the appearance of your workspace</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryColor">Primary Color</Label>
                <div className="flex items-center space-x-2">
                  <Input id="primaryColor" value="#DC2626" className="w-20" />
                  <div className="w-8 h-8 rounded border bg-red-600"></div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accentColor">Accent Color</Label>
                <div className="flex items-center space-x-2">
                  <Input id="accentColor" value="#7C3AED" className="w-20" />
                  <div className="w-8 h-8 rounded border bg-violet-600"></div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                  <Building className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <Button variant="outline" size="sm">Upload Logo</Button>
                  <p className="text-xs text-muted-foreground mt-1">PNG, SVG up to 2MB</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button>Save Branding</Button>
            </div>
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="whitelabel" className="space-y-6">
          <WorkspaceWhitelabelManager />
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Security</span>
            </CardTitle>
            <CardDescription>Manage workspace security and access controls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Require Two-Factor Authentication</div>
                <div className="text-sm text-muted-foreground">All members must enable 2FA</div>
              </div>
              <Switch />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Single Sign-On (SSO)</div>
                <div className="text-sm text-muted-foreground">Enable SSO authentication</div>
              </div>
              <Switch />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Public Access</div>
                <div className="text-sm text-muted-foreground">Allow public access to certain resources</div>
              </div>
              <Switch />
            </div>

            <div className="space-y-2">
              <Label htmlFor="allowedDomains">Allowed Email Domains</Label>
              <Input 
                id="allowedDomains" 
                placeholder="example.com, company.org"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Only users with these email domains can join this workspace
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <span>Danger Zone</span>
            </CardTitle>
            <CardDescription>Irreversible actions that affect your workspace</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-destructive rounded-lg">
              <div>
                <div className="font-medium">Transfer Ownership</div>
                <div className="text-sm text-muted-foreground">Transfer workspace ownership to another member</div>
              </div>
              <Button variant="outline" size="sm">Transfer</Button>
            </div>

            <div className="flex items-center justify-between p-4 border border-destructive rounded-lg">
              <div>
                <div className="font-medium">Delete Workspace</div>
                <div className="text-sm text-muted-foreground">Permanently delete this workspace and all data</div>
              </div>
              <Button variant="destructive" size="sm">Delete Workspace</Button>
            </div>
          </CardContent>
        </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}